import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { environment } from '@quorum/environments';
import { Employee, MarketingStoreSettings, Template } from '@quorum/models/xs-resource';
import { RouterStateService } from '@quorum/sha-router';
import { TemplatesFacade } from '@quorum/xsr-template-library';
// @ts-ignore
import * as grapesjs from 'grapesjs';
import { AuthenticatedUser } from 'libs/authentication/src/lib/+state/authentication.interfaces';
import { catchError, filter, map, skipWhile, take, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { DocParser } from '../../../../../server/api/doc-parser.class';
import messageTypeDefinitions from '../../../../../server/serverless/aws/stand-alone/communicator/resource-definition/definition.json';
import { DeleteTemplateDialogComponent } from '../delete-template-dialog/delete-template-dialog.component';
import { SendEmailPreviewDialogComponent } from '../send-email-preview-dialog/send-email-preview-dialog.component';

@Component({
  selector: 'xsr-full-editor',
  templateUrl: './full-editor.component.html',
  styleUrls: ['./full-editor.component.scss'],
  providers: [DocParser],
})
export class FullEditorComponent implements OnInit, OnDestroy {
  @Input() quorumGalleryTemplate: boolean;
  @Input() quorumLibraryTemplate: boolean;
  template: Template;
  currentUser: AuthenticatedUser;
  currentEmployee: Employee;
  editor: any;
  selectedLanguage: string;
  layoutView = 'layout';
  templateForm: FormGroup = new FormGroup({
    name: new FormControl(null, Validators.required),
    description: new FormControl(null, Validators.required),
    subject: new FormControl(null, Validators.required),
    messageTypeId: new FormControl(null, Validators.required),
  });
  needRefresh: boolean;

  // Single source of truth for the placeholder shown in the "Dealership Logo"
  // block when (a) editing a Quorum library/gallery template (substituted at
  // template-duplication time) or (b) a dealer has no letterhead configured
  // in HQ yet. Server-side template duplication and the per-rooftop migration
  // are responsible for replacing this with a real dealer URL.
  private static readonly DEALER_LOGO_PLACEHOLDER_URL =
    'https://quorum-video-transcoded-prod.s3.amazonaws.com/images/REPLACE';

  // Component lifecycle — every subscription pipes through takeUntil(destroy$).
  private readonly destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService,
    private dialog: MatDialog,
    private docParser: DocParser,
    private http: HttpClient,
    private route: ActivatedRoute,
    private routerStateService: RouterStateService,
    public templateFacade: TemplatesFacade
  ) {}

  ngOnDestroy(): void {
    // grapesjs binds resize/scroll/frame listeners to the shared SPA window;
    // destroy() unbinds them so they can't fire against the detached canvas
    // iframe after Angular removes #gjs.
    this.editor?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Resolves the right letterhead URL for the current dealer + rooftop combo.
   * Per-rooftop entry wins, falls back to dealer-default, then placeholder.
   * Errors from the settings endpoint resolve to the placeholder so the
   * "Dealership Logo" block always registers in the editor sidebar.
   */
  private resolveDealerLogoUrl(destinationId: string, rooftopId: number): Observable<string> {
    const placeholder = FullEditorComponent.DEALER_LOGO_PLACEHOLDER_URL;
    return this.apiService
      .get(`/v/1/system/settings/marketing/stores/${destinationId}`)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        map((settings: MarketingStoreSettings) => {
          const rooftopUrl = settings?.dealerLogosByRooftop?.[String(rooftopId)]?.url;
          const defaultUrl = settings?.dealerLogo?.url;
          return rooftopUrl ?? defaultUrl ?? placeholder;
        }),
        catchError(() => of(placeholder))
      );
  }

  ngOnInit() {
    this.routerStateService
      .selectRouterState()
      .pipe(take(1))
      .subscribe((routerState) => {
        this.selectedLanguage = routerState.state.root.queryParams?.language
          ? routerState.state.root.queryParams?.language
          : 'en';

        if (this.quorumGalleryTemplate || this.quorumLibraryTemplate) {
          combineLatest([
            this.authenticationStateService.selectQuorumApiKey(),
            this.authenticationStateService.selectQuorumDestinationId(),
          ]).subscribe(([key, destination]) => {
            this.initializeControl(destination, key);
          });
        } else {
          this.authenticationStateService
            .selectAuthenticatedEmployee()
            .pipe(filter((employee) => employee != null))
            .subscribe((employee: Employee) => {
              this.currentEmployee = employee;

              this.authenticationStateService
                .selectAuthenticatedUser()
                .pipe(take(1))
                .subscribe((user) => {
                  this.routerStateService
                    .selectRouterState()
                    .pipe(take(1))
                    .subscribe((routerState) => {
                      this.selectedLanguage = routerState.state.root.queryParams?.language
                        ? routerState.state.root.queryParams?.language
                        : 'en';
                      this.currentUser = user;
                      this.initializeControl(user.user.destination, user.user.apiKey);
                    });
                });
            });
        }
      });
  }

  initializeControl(destinationId: string, apiKey: string): void {
    let that = this;

    if (this.quorumGalleryTemplate) {
      this.templateFacade.selectQuorumGalleryTemplate(this.routerStateService.getFullTreeParams(this.route).templateId);
    } else if (this.quorumLibraryTemplate) {
      this.templateFacade.selectQuorumLibraryTemplate(this.routerStateService.getFullTreeParams(this.route).templateId);
    } else {
      this.templateFacade.selectTemplate(
        destinationId,
        this.routerStateService.getFullTreeParams(this.route).templateId
      );
    }

    const editor = grapesjs.init({
      container: '#gjs',
      plugins: ['@quorum/grapesjs-mjml'],
      pluginsOpts: {
        ['@quorum/grapesjs-mjml']: {},
      },
      style: '',
      height: '100%',
      //@ts-ignore
      layerManager: {
        appendTo: '#layers-container',
      },
      blockManager: {
        appendTo: '#blocks-container',
      },
      styleManager: {
        appendTo: '#styles-container',
      },
      traitManager: {
        appendTo: '#traits-container',
      },
      panels: {
        defaults: [
          {
            id: 'options',
            buttons: [
              {
                active: false,
                id: 'export-template',
                className: 'fa fa-code',
                command: 'export-template',
                context: 'export-template',
                attributes: { title: 'Export Template' },
              },
              {
                id: 'visibility',
                active: true,
                className: 'fa fa-square-o',
                command: 'sw-visibility',
                attributes: {
                  title: 'Show Layout Grid',
                },
              },
            ],
          },
        ],
      },
      assetManager: {
        assets: [],
        upload: `${this.apiService.getApiUrl()}/v/1/common/html-template-resources/${destinationId}/${
          this.routerStateService.getFullTreeParams(this.route).templateId
        }`,
        uploadName: 'files',
        multiUpload: true,
        headers: {
          'destination-id': destinationId,
          'api-key': apiKey,
        },
      },
      storageManager: {
        type: 'quorum',
        autosave: false,
        autoload: false,
      },
    });
    this.editor = editor;

    const assetManager = editor.AssetManager;
    const blockManager = editor.BlockManager;
    const commands = editor.Commands;
    const storageManager = editor.StorageManager;
    const panels = editor.Panels;

    //@ts-ignore
    editor.DomComponents.addType('mj-image', {
      model: {
        defaults: {
          void: false,
        },
      },
    });

    this.apiService
      .get(
        `v/1/common/html-template-resources/${destinationId}/${
          this.routerStateService.getFullTreeParams(this.route).templateId
        }`
      )
      .subscribe((templateAssets) => {
        assetManager.add(templateAssets);
      });

    //@ts-ignore
    let blocks = blockManager.getAll();
    blocks.forEach((block: any) => block.set('category', 'General Blocks'));
    //@ts-ignore
    blockManager.render();

    blockManager.remove('mj-image');
    blockManager.remove('mj-raw');
    blockManager.remove('mj-social-element');
    blockManager.remove('mj-social-group');

    blockManager.add('mj-social', {
      label: 'Social Group',
      category: 'Social Media',
      content: `
        <mj-social border-radius="12px" font-size="15px" icon-size="30px" mode="horizontal">
          <mj-social-element name="facebook-noshare" href="{{dealer.facebook}}"></mj-social-element>
          <mj-social-element name="twitter-noshare" href="{{dealer.twitter}}"></mj-social-element>
          <mj-social-element name="instagram" href="{{dealer.instagram}}"></mj-social-element>
        </mj-social>
         `,
      attributes: { class: 'fa fa-share-alt' },
    });

    blockManager.add('mj-facebook', {
      label: 'Facebook',
      category: 'Social Media',
      content: '<mj-social-element name="facebook-noshare" href="{{dealer.facebook}}"></mj-social-element>',
      attributes: { class: 'fa fa-image' },
    });

    blockManager.add('mj-instagram', {
      label: 'Instagram',
      category: 'Social Media',
      content: '<mj-social-element name="instagram" href="{{dealer.instagram}}"></mj-social-element>',
      attributes: { class: 'fa fa-image' },
    });

    blockManager.add('mj-x', {
      label: 'X (Twitter)',
      category: 'Social Media',
      content:
        '<mj-social-element src="https://quorum-template-logo-resources.s3.amazonaws.com/twitter-x-logo.png" href="{{dealer.twitter}}"></mj-social-element>',
      attributes: { class: 'fa fa-image' },
    });

    blockManager.add('mj-youtube', {
      label: 'Youtube',
      category: 'Social Media',
      content: '<mj-social-element name="youtube" href="{{dealer.youtube}}"></mj-social-element>',
      attributes: { class: 'fa fa-image' },
    });

    blockManager.add('mj-image', {
      label: 'Image',
      category: 'General Blocks',
      content: '<mj-image alt="image" src="http://placehold.it/350x250/78c5d6/fff"></mj-image>',
      attributes: { class: 'fa fa-image' },
    });

    commands.add('save', (editor: any) => {
      let markup = document
        .getElementsByTagName('iframe')[0]
        .contentWindow.document.getElementsByTagName('body')[0].outerHTML;

      this.http
        .post(
          `${environment.htmlConversionUrl}/convertToImage`,
          {
            html: markup,
            options: { type: 'jpeg', fullPage: false, height: 720, width: 600 },
          },
          { responseType: 'blob' }
        )
        .subscribe(
          (data) => {
            let reader = new FileReader();
            reader.readAsDataURL(data);
            reader.onloadend = () => {
              var base64data = reader.result;
              this.apiService
                .post(
                  `v/1/common/html-template-resources/thumbnail/${destinationId}/${
                    this.routerStateService.getFullTreeParams(that.route).templateId
                  }`,
                  { image: base64data }
                )
                .subscribe((response) => {
                  this.template = {
                    ...this.template,
                    languageDefinitionMap: {
                      ...this.template.languageDefinitionMap,
                      [this.selectedLanguage]: {
                        ...this.template.languageDefinitionMap[this.selectedLanguage],
                        description: this.templateForm.get('description').value,
                        name: this.templateForm.get('name').value,
                        subject: this.templateForm.get('subject').value,
                        previewImageUrl: response.url,
                      },
                    },
                  };

                  editor.store();
                });
            };
          },
          (error) => console.log(error)
        );
    });
    commands.add('delete', (editor: any) => {
      let dialogRef = that.dialog.open(DeleteTemplateDialogComponent, {
        data: { template: that.template },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (!(this.quorumGalleryTemplate || this.quorumLibraryTemplate)) {
            this.templateFacade.deleteSelectedTemplate(
              destinationId,
              that.routerStateService.getFullTreeParams(that.route).templateId
            );
          }
          //// NEED TO DELETE RESOURCES TOO!!!
        }
      });
    });

    storageManager.add('quorum', {
      load(keys: any, clb: any, clbErr: any) {
        that.templateFacade.selectedTemplate$
          .pipe(
            filter((selectedTemplate) => selectedTemplate !== null),
            skipWhile(
              (selectedTemplate) =>
                selectedTemplate.template.id !== that.routerStateService.getFullTreeParams(that.route).templateId
            ),
            take(1)
          )
          .subscribe((selectedTemplate) => {
            that.template = selectedTemplate.template;
            clb(selectedTemplate.template);
          });
      },

      store(keys: any, clb: any, clbErr: any) {
        let pendingTemplate = {
          ...that.template,
        };

        pendingTemplate.languageDefinitionMap[that.selectedLanguage] = {
          ...pendingTemplate.languageDefinitionMap[that.selectedLanguage],
          html: editor.runCommand('mjml-get-code').html,
          mjml: editor.getHtml(),
        };

        that.templateFacade.selectedTemplate$
          .pipe(
            take(1),
            map((selectedTemplate) => selectedTemplate.etag)
          )
          .subscribe((etag) => {
            if (that.quorumLibraryTemplate) {
              that.templateFacade.updateSelectedQuorumLibraryTemplate(etag, pendingTemplate);
            } else if (that.quorumGalleryTemplate) {
              that.templateFacade.updateSelectedQuorumGalleryTemplate(etag, pendingTemplate);
            } else {
              that.templateFacade.updateSelectedTemplate(destinationId, etag, pendingTemplate);
            }
            that.templateFacade.selectedTemplate$
              .pipe(
                skipWhile((selectedTemplate) => selectedTemplate.etag === etag),
                take(1)
              )
              .subscribe((selectedTemplate) => clb());
          });
      },
    });

    // panels.removeButton('options', 'mjml-import');
    // panels.removeButton('options', 'export-template');
    panels.addButton('options', {
      id: 'saveButton',
      className: 'fa fa-save',
      command: 'save',
      attributes: {
        title: 'Save',
      },
      active: false,
    });
    panels.addButton('options', {
      id: 'deleteButton',
      className: 'fa fa-trash-o',
      command: 'delete',
      attributes: {
        title: 'Delete',
      },
      active: false,
    });

    panels.addButton('devices-c', {
      id: 'browsepreviewButton',
      className: 'fa fa-chrome',
      command: 'browserPreview',
      attributes: {
        title: 'Browser Preview',
      },
      active: false,
    });

    panels.addButton('devices-c', {
      id: 'emailPreviewButton',
      className: 'fa fa-envelope-o',
      command: 'emailPreview',
      attributes: {
        title: 'Email Preview',
      },
      active: false,
    });

    editor.on('component:add', (component: any) => {
      // to prevent 'columnizable' default
      switch (component.get('type')) {
        case 'mj-navbar-link':
        case 'mj-social-element':
        case 'mj-column':
          this.needRefresh = true;
          break;
        default:
        //nothing
      }
    });

    editor.on('update', () => {
      // to prevent 'columnizable' elements render default
      if (this.needRefresh) {
        editor.setComponents(editor.getHtml());
        this.needRefresh = false;
      }
    });

    editor.on('asset:remove', (response: any) => {
      let fileName = response.id.split('/')[5];
      this.apiService
        .delete(
          `v/1/common/html-template-resources/${destinationId}/${
            this.routerStateService.getFullTreeParams(that.route).templateId
          }/${fileName}`
        )
        .subscribe((response) => console.log(response));
    });
    editor.on('storage:load', (response: any) => {
      editor.setComponents(response.languageDefinitionMap[this.selectedLanguage].mjml);
      this.template = response;
      this.templateForm.setValue({
        name: response.languageDefinitionMap[this.selectedLanguage].name,
        description: response.languageDefinitionMap[this.selectedLanguage].description
          ? response.languageDefinitionMap[this.selectedLanguage].description
          : '',
        messageTypeId: response.messageTypeId,
        subject: response.languageDefinitionMap[this.selectedLanguage].subject
          ? response.languageDefinitionMap[this.selectedLanguage].subject
          : '',
      });

      this.templateForm.markAllAsTouched();
      const messageTypeId = response.messageTypeId;

      commands.add('browserPreview', (editor: any) => {
        this.sendPreviewToBrowser(messageTypeId, editor);
      });

      commands.add('emailPreview', (editor: any) => {
        let dialogRef = that.dialog.open(SendEmailPreviewDialogComponent);

        dialogRef.afterClosed().subscribe((result) => {
          this.sendPreviewToEmail(result.fromEmail, result.toEmails, result.subject, messageTypeId, editor);
        });
      });

      if (messageTypeDefinitions[messageTypeId].attributes.calendarPlaceholder) {
        blockManager.add('mj-calendar', {
          label: 'Calendar Button',
          content: `<mj-button font-family="Helvetica" background-color="#00538f" color="white" href="${messageTypeDefinitions[messageTypeId].attributes.calendarPlaceholder}">
                  Add to Calendar
                 </mj-button>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-calendar' },
        });
      }

      if (messageTypeDefinitions[messageTypeId].attributes.onlinePaymentPlaceholder) {
        blockManager.add('mj-calendar', {
          label: 'Payment Button',
          content: `<mj-button font-family="Helvetica" background-color="#00538f" color="white" href="${messageTypeDefinitions[messageTypeId].attributes.onlinePaymentPlaceholder}">
                  Pay Online
                 </mj-button>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-credit-card' },
        });
      }

      if (messageTypeDefinitions[messageTypeId].attributes.unsubscribePlaceholder) {
        blockManager.add('mj-unsub', {
          label: 'Unsubscribe Link',
          content: `
          <mj-text align="center" font-size="11px" line-height="14px">
            <a href="{{unsubscribe.url}}" title="Unsubscribe" target="_blank" class="link">Click Here</a> to Unsubscribe
            <div>
              <a href="{{unsubscribe.url}}" title="Manage" target="_blank" class="link">Manage Message Preferences</a>
            </div>
          </mj-text>
          `,
          category: `General Blocks`,
          attributes: { class: 'fa fa-envelope-o' },
        });
      }

      if (messageTypeDefinitions[messageTypeId].attributes.digitalCheckInPlaceholder) {
        blockManager.add('mj-digital-check-in', {
          label: 'Digital Check In',
          content: `<mj-button css-class="digitalCheckInPlaceholder" font-family="Helvetica" background-color="#015a9c" color="white" border-radius="10px 10px 10px 10px" padding-top="0" padding-bottom="0"  href="${messageTypeDefinitions[messageTypeId].attributes.digitalCheckInPlaceholder}">
                  Check In
                 </mj-button>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-calendar-check-o' },
        });

        blockManager.add('mj-digital-check-status', {
          label: 'View Repair Status',
          content: `<mj-button css-class="digitalCheckInPlaceholder" font-family="Helvetica" background-color="#015a9c" color="white" border-radius="10px 10px 10px 10px" padding-top="0" padding-bottom="0"  href="${messageTypeDefinitions[messageTypeId].attributes.digitalCheckInPlaceholder}">
                  View Repair Status
                 </mj-button>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-calendar-check-o' },
        });
      }

      if (messageTypeId == 2) {
        blockManager.add('mj-book-dealermine', {
          label: 'Book Appointment',
          content: `<mj-button css-class="bookDealermineAppointmentPlaceholder" font-family="Helvetica" background-color="#00538f" color="white" border-radius="10px 10px 10px 10px" padding-top="0" padding-bottom="0" href="https://eservicemobi.dealermine.net/?dealer=smdemo&language=EN">
                  Book Appointment
                 </mj-button>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-calendar' },
        });
      }

      if (messageTypeId == 23) {
        blockManager.add('mj-view-vcr', {
          label: 'View VCR',
          content: `<mj-button font-family="Helvetica" background-color="#00538f" color="white" border-radius="10px 10px 10px 10px" padding-top="0" padding-bottom="0" href="${messageTypeDefinitions[messageTypeId].attributes.vehicleConditionReportUrlPlaceholder}">
                  View Vehicle Condition Report
                 </mj-button>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-ambulance' },
        });
      }

      if ([5, 9, 12].includes(messageTypeId)) {
        blockManager.add('mj-appointment-cases', {
          label: 'Appointment Cases',
          content: `<mj-section data-gjs-custom-name="Appointment Cases"><mj-column data-gjs-selectable="false"  data-gjs-editable="false" data-gjs-hoverable="false" data-gjs-resizable="false" data-gjs-highlightable="false" css-class="${messageTypeDefinitions[messageTypeId].resources.dependencies.serviceAppointmentCases.repeaterClass}"><mj-text  data-gjs-editable="false" data-gjs-hoverable="false" data-gjs-resizable="false" data-gjs-highlightable="false" data-gjs-editable="false" data-gjs-selectable="false" >{{serviceAppointmentCases.sequence}} - {{serviceAppointmentCases.requestDescription}}</mj-text></mj-column></mj-section>`,
          category: `${messageTypeDefinitions[messageTypeId].description} Blocks`,
          attributes: { class: 'fa fa-wrench' },
        });
      }

      blockManager.add('mj-dealership-info', {
        label: 'Dealership Info',
        content: `
                <mj-text>
                  <p>{{dealer.firstName}}</p>
                  <p><a href="https://maps.google.com/?q={{dealerAddress.addressLine1}},{{dealerAddress.provStateId}},{{dealerAddress.countryId}}" target="_blank">{{dealerAddress.addressLine1}}</a></p>
                  <p><a href="tel:{{dealerAddress.homePhoneNumber}}" target="_blank">{{dealerAddress.homePhoneNumber}}</a> | <a href="mailto:{{dealer.emailAddress}}" target="_blank">{{dealer.emailAddress}}</a></p>
                  <p><a href="{{dealer.webAddress}}" target="_blank">{{dealer.webAddress}}</a></p>
                </mj-text>
          `,
        category: `General Blocks`,
        attributes: { class: 'fa fa-info' },
      });

      // "Dealership Logo" block — always registered so the sidebar is consistent.
      // Library/gallery templates use the static placeholder; store templates
      // resolve to the dealer's real letterhead (per-rooftop wins over default).
      const registerDealerLogoBlock = (logoUrl: string): void => {
        blockManager.add('mj-dealer-logo', {
          label: 'Dealership Logo',
          category: 'General Blocks',
          content: `<mj-image alt="Logo" crossOrigin src="${logoUrl}"></mj-image>`,
          attributes: { class: 'fa fa-image' },
        });
      };

      if (that.quorumGalleryTemplate || this.quorumLibraryTemplate) {
        registerDealerLogoBlock(FullEditorComponent.DEALER_LOGO_PLACEHOLDER_URL);
      } else {
        this.resolveDealerLogoUrl(destinationId, this.currentEmployee.rooftopId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(registerDealerLogoBlock);
      }

      let rteOptions = {
        icon: '',
        attributes: {
          title: 'Placeholders',
        },
        event: 'change',
        result: (rte: any, action: any) => {
          return rte.insertHTML(action.btn.firstChild.nextSibling.value);
        },
        update: (rte: any, action: any) => {
          action.btn.firstChild.nextSibling.value = '';
        },
      };

      rteOptions.icon = `
      <select name="placeholders" id="placeholders" style="color:#FFF;" class="gjs-field">
        <option value="">--Placeholders--<option>
      `;
      // console.log(messageTypeId, messageTypeDefinitions[messageTypeId].resources);

      let source = Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0];
      let apiLookup1 =
        messageTypeDefinitions[messageTypeId].resources.source[
          Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0]
        ].apiResource.split('/')[0];

      let apiLookup2 =
        messageTypeDefinitions[messageTypeId].resources.source[
          Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0]
        ].apiResource.split('/')[1];

      const camel2title = (camelCase: string) =>
        camelCase.replace(/([A-Z])/g, (match) => ` ${match}`).replace(/^./, (match) => match.toUpperCase());

      let def = this.docParser.getPropAndDescriptions(apiLookup1, `/${apiLookup2}`, 2);

      rteOptions.icon += `<optgroup label="${camel2title(source)}">`;

      Object.keys(def.primaryResource)
        .filter((key) => def.primaryResource[key]['x-template-placeholder'])
        .forEach((key) => {
          if (def.primaryResource[key].format == 'date-time') {
            rteOptions.icon += `<option value={{${source}.${key}?format=time}} title="${
              def.primaryResource[key].description
            }">${camel2title(key)
              .replace(/([A-Z]+)/g, ' $1')
              .replace(/([A-Z][a-z])/g, ' $1')} - Time (eg. '5:30 PM')</option>`;

            rteOptions.icon += `<option value={{${source}.${key}?format=day}} title="${
              def.primaryResource[key].description
            }">${camel2title(key)
              .replace(/([A-Z]+)/g, ' $1')
              .replace(/([A-Z][a-z])/g, ' $1')} - Day (eg. 'Monday')</option>`;

            rteOptions.icon += `<option value={{${source}.${key}?format=fullDate}} title="${
              def.primaryResource[key].description
            }">${camel2title(key)
              .replace(/([A-Z]+)/g, ' $1')
              .replace(/([A-Z][a-z])/g, ' $1')} - Full Date (eg. 'Mar. 15th, 2021')</option>`;
          } else {
            rteOptions.icon += `<option value={{${source}.${key}}} title="${
              def.primaryResource[key].description
            }">${camel2title(key)
              .replace(/([A-Z]+)/g, ' $1')
              .replace(/([A-Z][a-z])/g, ' $1')}</option>`;
          }
        });

      if (
        def.embeds &&
        messageTypeDefinitions[messageTypeId].resources.source[
          Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0]
        ].embedded
      ) {
        Object.keys(def.embeds).forEach((embed) => {
          rteOptions.icon += `<optgroup label="${camel2title(apiLookup2)} - ${camel2title(embed)}">`;

          Object.keys(def.embeds[embed]).forEach((key) => {
            rteOptions.icon += `<option value={{${apiLookup2}.embedded.${embed}.${key}}} title="${
              def.embeds[embed][key].description
            }">${camel2title(key)
              .replace(/([A-Z]+)/g, ' $1')
              .replace(/([A-Z][a-z])/g, ' $1')}</option>`;
          });
        });
      }

      Object.keys(messageTypeDefinitions[parseInt(messageTypeId)].resources.dependencies).forEach((dep) => {
        let nestedApiLookup1 =
          messageTypeDefinitions[messageTypeId].resources.dependencies[dep].apiResource.split('/')[0];

        let nestedApiLookup2 =
          messageTypeDefinitions[messageTypeId].resources.dependencies[dep].apiResource.split('/')[1];

        let def = this.docParser.getPropAndDescriptions(nestedApiLookup1, `/${nestedApiLookup2}`, 2);

        rteOptions.icon += `<optgroup label="${camel2title(dep)}">`;
        Object.keys(def.primaryResource)
          .filter((key) => def.primaryResource[key]['x-template-placeholder'])
          .forEach((key) => {
            if (def.primaryResource[key].format == 'date-time') {
              rteOptions.icon += `<option value="{{${dep}.${key}?format=time}}" title="${
                def.primaryResource[key].description
              }">${camel2title(key)
                .replace(/([A-Z]+)/g, ' $1')
                .replace(/([A-Z][a-z])/g, ' $1')} - Time (eg. '5:30 PM')</option>`;

              rteOptions.icon += `<option value="{{${dep}.${key}?format=day}}" title="${
                def.primaryResource[key].description
              }">${camel2title(key)
                .replace(/([A-Z]+)/g, ' $1')
                .replace(/([A-Z][a-z])/g, ' $1')} - Day (eg. 'Monday')</option>`;

              rteOptions.icon += `<option value="{{${dep}.${key}?format=fullDate}}" title="${
                def.primaryResource[key].description
              }">${camel2title(key)
                .replace(/([A-Z]+)/g, ' $1')
                .replace(/([A-Z][a-z])/g, ' $1')} - Full Date (eg. 'Mar. 15th, 2021')</option>`;
            } else {
              rteOptions.icon += `<option value="{{${dep}.${key}}}" title="${
                def.primaryResource[key].description
              }">${camel2title(key)
                .replace(/([A-Z]+)/g, ' $1')
                .replace(/([A-Z][a-z])/g, ' $1')}</option>`;
            }
          });
        rteOptions.icon += `</optgroup>`;
      });

      rteOptions.icon += `</select> `;
      editor.RichTextEditor.add('placeholders', rteOptions);
    });
    editor.on('run:save:before', (options: any) => {
      if (this.templateForm.invalid) {
        this.templateFacade.addToast({
          message: 'Missing Required Fields.',
        });
        options.abort = true;
        return;
      }
      //if (this.quorumGalleryTemplate || this.quorumLibraryTemplate) {

      that.apiService
        .getWithHeaders(
          this.quorumGalleryTemplate
            ? `v/1/quorum/templates/gallery/${that.routerStateService.getFullTreeParams(that.route).templateId}`
            : this.quorumLibraryTemplate
            ? `v/1/quorum/templates/library/${that.routerStateService.getFullTreeParams(that.route).templateId}`
            : `v/1/quorum/templates/store-gallery/${destinationId}/${
                that.routerStateService.getFullTreeParams(that.route).templateId
              }`
        )
        .subscribe((response: any) => {
          that.templateFacade.selectedTemplate$
            .pipe(
              take(1),
              map((selectedTemplate) => selectedTemplate.etag)
            )
            .subscribe((etag) => {
              if (response.headers.etag !== etag) {
                this.templateFacade.addToast({
                  message: 'Template Save Conflict.',
                });
                options.abort = true;
              }
            });
        });
    });
    editor.on('storage:end:store', (response: any) => {});

    editor.load();
  }

  selectLanguage(language: string) {
    if (!this.template.languageDefinitionMap[language])
      this.template.languageDefinitionMap[language] = { ...this.template.languageDefinitionMap['en'] };

    this.editor.setComponents(this.template.languageDefinitionMap[language].mjml);
    this.templateForm.setValue({
      name: this.template.languageDefinitionMap[language].name,
      description: this.template.languageDefinitionMap[language].description
        ? this.template.languageDefinitionMap[language].description
        : '',
      messageTypeId: this.template.messageTypeId,
      subject: this.template.languageDefinitionMap[language].subject
        ? this.template.languageDefinitionMap[language].subject
        : '',
    });

    this.routerStateService.go('', {
      queryParams: { language: language },
      queryParamsHandling: 'merge',
    });
  }
  generatePreview(messageTypeId: string, editor: any) {
    let resources = {};

    let source = Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0];

    let apiLookup1 =
      messageTypeDefinitions[messageTypeId].resources.source[
        Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0]
      ].apiResource.split('/')[0];

    let apiLookup2 =
      messageTypeDefinitions[messageTypeId].resources.source[
        Object.keys(messageTypeDefinitions[messageTypeId].resources.source)[0]
      ].apiResource.split('/')[1];

    resources[source] = DocParser.getExampleData(apiLookup1, `/${apiLookup2}`, 'get', 2);

    Object.keys(messageTypeDefinitions[parseInt(messageTypeId)].resources.dependencies).forEach((dep) => {
      let nestedApiLookup1 =
        messageTypeDefinitions[messageTypeId].resources.dependencies[dep].apiResource.split('/')[0];

      let nestedApiLookup2 =
        messageTypeDefinitions[messageTypeId].resources.dependencies[dep].apiResource.split('/')[1];

       resources[dep] = DocParser.getExampleData(nestedApiLookup1, `/${nestedApiLookup2}`, 'get', 2);
    });

    let html = editor.runCommand('mjml-get-code').html;
    const placeholders: string[] = this.findPlaceholders(html);

    placeholders.forEach((placeholder: string) => {
      let placeholderResource = placeholder.replace('{{', '').replace('}}', '').split('.')[0];
      let placeholderPropertyWithQuery = placeholder.replace('{{', '').replace('}}', '?').split('.')[1];
      let placeholderPropertyWithoutQuery = placeholderPropertyWithQuery.substring(
        0,
        placeholderPropertyWithQuery.indexOf('?')
      );

      let exampleData = '';

      switch (placeholderResource) {
        case 'calendar':
        case 'payment':
        case 'digitalCheckIn':
        case 'vcr':
        case 'unsubscribe':
          exampleData = '';
          break;
        default:
          if (
            placeholderResource === 'dealer' &&
            ['instagram', 'twitter', 'facebook', 'youtube'].includes(placeholderPropertyWithoutQuery)
          ) {
            console.log(placeholderPropertyWithoutQuery);
            exampleData = '';
          }
          exampleData = this.formatAttributeValue(
            placeholder,
            resources[placeholderResource][placeholderPropertyWithoutQuery]
          );
          break;
      }

      const regex = new RegExp(`${placeholder}`.replace('?', '\\?'), 'gi');
      html = html.replace(regex, exampleData || '');
    });
    return html;
  }

  sendPreviewToBrowser(messageTypeId: string, editor: any) {
    let tab = window.open();
    tab.document.open().write(this.generatePreview(messageTypeId, editor));
  }

  sendPreviewToEmail(fromEmail: string, toEmails: string, subject: string, messageTypeId: string, editor: any) {
    let email = {
      message: this.generatePreview(messageTypeId, editor),
      subject: subject,
      toEmailAddresses: toEmails.split(','),
    };

    this.http
      .post('https://xm9sfwhn09.execute-api.us-east-1.amazonaws.com/dev/template-editor-send-email', email)
      .subscribe((d) => {
        console.log(d);
      });
  }

  findPlaceholders(markup: string): string[] {
    const regex = /{{[a-z0-9.="?]+}}/gi;
    const re: RegExp = new RegExp(regex);
    let placeholders: string[] = [];
    let temp;
    while ((temp = re.exec(markup))) {
      placeholders.push(temp[0]);
    }
    return placeholders;
  }

  formatAttributeValue(placeholder: string, attributeValue: string): string {
    if (!attributeValue) return null;
    if (placeholder.toUpperCase().includes('PHONENUMBER')) {
      return attributeValue.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }

    if (placeholder.includes('?format=')) {
      if (placeholder.includes(`?format=time`)) {
        const myDate = new Date(attributeValue);
        let time: string = myDate
          .toLocaleDateString('en-US', { hour: '2-digit', minute: '2-digit' })
          .split(',')
          .pop()
          .trim();
        if (time.charAt(0) === '0') time = time.substring(1);
        return time;
      } else if (placeholder.includes(`?format=day`)) {
        const myDate = new Date(attributeValue);
        return myDate.toLocaleDateString('en-US', { weekday: 'long' });
      } else if (placeholder.includes(`?format=fullDate`)) {
        const myDate = new Date(attributeValue);
        const options: any = { year: 'numeric', month: 'short', day: 'numeric' };
        return myDate.toLocaleDateString('en-US', options);
      }
    }

    switch (placeholder.toUpperCase()) {
      case `{{bodyShopHoursOfOperation.description}}`.toUpperCase():
      case `{{partsHoursOfOperation.description}}`.toUpperCase():
      case `{{salesHoursOfOperation.description}}`.toUpperCase():
      case `{{serviceHoursOfOperation.description}}`.toUpperCase():
        const regex = new RegExp('\r', 'gi');
        attributeValue = attributeValue.replace(regex, '<br>');
        break;

      default:
        break;
    }
    return attributeValue;
  }
}
